import { Experience, PersonalInfo, Study }               from '../../api'
import { api }                                           from '../../client'
import { convertChangePassword }                         from '../../components/forms/PasswordChangeForm'
import { convertEditExperience, convertToEditEducation } from '../../components/forms/schemas'
import { bind }                                          from '../../utils'
import createAction                                      from '../redux-utils/create-action'
import { Reducer }                                       from '../types'
import { createRequest }                                 from './communication.duck'
import { ApplicationState }                              from './index'

/* ACTION TYPES */

const ACCOUNT = 'account'

export const PERSONAL_INFO_GET_SUCCESS = `[${ACCOUNT}] PERSONAL_INFO_GET_SUCCESS`
export const PERSONAL_INFO_EDITED = `[${ACCOUNT}] PERSONAL_INFO_EDITED`
export const EDUCATION_GET_SUCCESS = `[${ACCOUNT}] EDUCATION_GET_SUCCESS`
export const EDUCATION_EDITED = `[${ACCOUNT}] EDUCATION_EDITED`
export const EXPERIENCE_GET_SUCCESS = `[${ACCOUNT}] EXPERIENCE_GET_SUCCESS`
export const EXPERIENCE_EDITED = `[${ACCOUNT}] EXPERIENCE_EDITED`
export const CONTRACT_ACCEPTED = `[${ACCOUNT}] CONTRACT_ACCEPTED`
export const PASSWORD_CHANGED = `[${ACCOUNT}] PASSWORD_CHANGED`
export const LASTACCESS_GET_SUCCESS = `[${ACCOUNT}] LASTACCESS_GET_SUCCESS`

export interface AccountState {
  personalInformation?: PersonalInfo
  education?: Study []
  experience?: Experience []
  currency?: string
  hasAcceptedContract?: boolean,
  lastAccess?:Date
}

const account: Reducer<AccountState> = (
  state = {},
  {
    type,
    payload,
  },
) => {
  switch (type) {
    case PERSONAL_INFO_GET_SUCCESS:
    case PERSONAL_INFO_EDITED:
      const { personalInformation } = payload

      return {
        ...state,
        personalInformation,
      }

    case EDUCATION_EDITED:
    case EDUCATION_GET_SUCCESS:
      const { education } = payload

      return {
        ...state,
        education,
      }

    case EXPERIENCE_EDITED:
    case EXPERIENCE_GET_SUCCESS:
      const { experiences } = payload

      return {
        ...state,
        experience: experiences,
      }

    case CONTRACT_ACCEPTED:
      return {
        ...state,
        hasAcceptedContract: true,
      }
    case LASTACCESS_GET_SUCCESS:
      const { lastAccess } = payload

      return {
        ...state,
        lastAccess: lastAccess,
      }
    default:
      return state
  }
}

export default account

/* ACTIONS */

export const agreeToContract = () =>
  createAction(CONTRACT_ACCEPTED)

export const handlePersonalInfoGet = response =>
  createAction(
    PERSONAL_INFO_GET_SUCCESS,
    { personalInformation: response },
  )

export const handlePersonalInfoEdited = (response, personalInformation) =>
  createAction(
    PERSONAL_INFO_EDITED,
    { personalInformation },
    {
      toast: {
        message: 'personalInfoUpdated',
      }
    },
  )

export const handleEducationGet = response =>
  createAction(
    EDUCATION_GET_SUCCESS,
    { education: response },
  )

export const handleEducationEdited = (response, type, editEducationPayload) =>
  createAction(
    EDUCATION_EDITED,
    editEducationPayload,
    {
      toast: {
        message: 'educationUpdated',
      }
    },
  )

export const handleExperienceGet = response =>
  createAction(
    EXPERIENCE_GET_SUCCESS,
    { experiences: response },
  )

export const handleExperienceEdited = (response, type, editExperiencePayload) =>
  createAction(
    EXPERIENCE_EDITED,
    editExperiencePayload,
    {
      toast: {
        message: 'experienceUpdated',
      }
    },
  )

export const handleLastAccessGet = response =>
  createAction(
    LASTACCESS_GET_SUCCESS,
    { lastAccess: response },
  )

const handlePasswordChanged = createAction(
  PASSWORD_CHANGED,
  null,
  {
    toast: {
      message: 'successfullyChangedPassword',
    },
  },
)

export const personalInformationGetRequest = createRequest({
  namespace:      `${ACCOUNT}-get`,
  request:        bind(api)(api.getPersonalInfo),
  successHandler: handlePersonalInfoGet,
})

export const personalInformationEditRequest = createRequest({
  namespace:      `${ACCOUNT}-personal-info-edit`,
  request:        bind(api)(api.updatePersonalInfo),
  successHandler: handlePersonalInfoEdited,
})

export const educationGetRequest = createRequest({
  namespace:      `${ACCOUNT}-education-get`,
  request:        bind(api)(api.getStudies),
  successHandler: handleEducationGet,
})

export const educationEditRequest = createRequest({
  namespace:      `${ACCOUNT}-education-edit`,
  request:        convertToEditEducation(bind(api)(api.updateStudies)),
  successHandler: handleEducationEdited,
})

export const experienceGetRequest = createRequest({
  namespace:      `${ACCOUNT}-experience-get`,
  request:        bind(api)(api.getExperience),
  successHandler: handleExperienceGet,
})

export const experienceEditRequest = createRequest({
  namespace:      `${ACCOUNT}-experience-edit`,
  request:        convertEditExperience(bind(api)(api.updateExperience)),
  successHandler: handleExperienceEdited,
})

export const changePasswordRequest = createRequest({
  namespace:      `${ACCOUNT}-change-password`,
  request:        convertChangePassword(bind(api)(api.updatePassword)),
  successHandler: handlePasswordChanged,
})

export const lastAccessGetRequest = createRequest({
  namespace:      `${ACCOUNT}-lastAccess-get`,
  request:        bind(api)(api.getLastAccess),
  successHandler: handleLastAccessGet,
})

/* SELECTORS */

export const selectExperience = ({ account: { experience } }: ApplicationState) =>
  experience

export const selectEducation = ({ account: { education } }: ApplicationState) =>
  education

export const selectPersonalInformation = ({ account: { personalInformation } }: ApplicationState) =>
  personalInformation

/*
 TODO:

 an account should probably be associated with a
 currency this doesn't exist in the service though
 so just default to Euro for now
 */
export const selectCurrency = ({ account: { currency } }: ApplicationState) =>
  currency || '€'

export const selectContractAccepted = ({ account: { hasAcceptedContract } }: ApplicationState) =>
  hasAcceptedContract



// WEBPACK FOOTER //
// ./src/redux/ducks/account.duck.ts